import { useEffect, useState } from "react"

/**
 * ProductFruits requires the user email as username.
 * Strip control chars and characters that could break downstream DOM/script handling.
 */
const sanitizeProductFruitsUsername = (email) => {
  if (typeof email !== "string") {
    return null
  }

  const sanitized = email.trim().replace(/[\u0000-\u001F\u007F<>\\"'`]/g, "")
  return sanitized.length > 0 ? sanitized : null
}

/**
 * Read the Rails CSP nonce exposed in the layout for dynamically injected scripts.
 */
const getCspNonce = () => {
  const cspMeta = document.querySelector('meta[name="csp-nonce"]')
  if (cspMeta?.content) {
    return cspMeta.content
  }

  const appScript = document.querySelector('script[nonce][src*="application"]')
  return appScript?.nonce || null
}

/**
 * Hook to load and initialize ProductFruits with user information
 * @param {Object} currentUser - The current user object
 * @param {boolean} isLoading - Whether user data is still loading
 */
const useProductFruits = (currentUser, isLoading) => {
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Step 1: Load the ProductFruits script
  useEffect(() => {
    // Check if already loaded
    if (window.$productFruits && Array.isArray(window.$productFruits)) {
      console.log("✅ Onboarding already loaded")
      setScriptLoaded(true)
      return
    }

    console.log("📦 Loading Onboarding script...")

    try {
      window.$productFruits = window.$productFruits || []
      window.productFruits = window.productFruits || {}
      window.productFruits.scrV = "2"

      console.log("✅ Onboarding objects initialized on window")

      // ProductFruits has no React SDK on our stack; client-side CDN load is intentional.
      const script = document.createElement("script")
      script.src = "https://app.productfruits.com/static/script.js"
      script.async = true

      const cspNonce = getCspNonce()
      if (cspNonce) {
        // Intentional: attach Rails CSP nonce to the fixed ProductFruits CDN script. NOSONAR
        script.nonce = cspNonce // NOSONAR javascript:S6268
      } else {
        console.warn("⚠️ CSP nonce not found; ProductFruits script may be blocked by CSP")
      }

      script.onload = () => {
        console.log("✅ Onboarding script loaded successfully")
        setScriptLoaded(true)
      }

      script.onerror = (error) => {
        console.error("❌ Failed to load Onboarding script:", error)
        console.error(
          "Check Network tab for details and CSP violations in console"
        )
      }

      // Fixed CDN URL, no user-controlled src; required because ProductFruits is client-only. NOSONAR
      document.head.appendChild(script) // NOSONAR javascript:S6268

      return () => {
        if (script.parentNode) {
          script.parentNode.removeChild(script)
        }
      }
    } catch (error) {
      console.error("❌ Error setting up Onboarding:", error)
    }
  }, [])

  // Step 2: Initialize ProductFruits with user data once script is loaded
  useEffect(() => {
    if (!scriptLoaded || isLoading || !currentUser || isInitialized) {
      return
    }

    console.log("🎯 Initializing Onboarding with user data...")

    const initTimeout = setTimeout(() => {
      try {
        if (!window.$productFruits || !Array.isArray(window.$productFruits)) {
          console.error("❌ onboarding api is not available after script load")
          return
        }

        const username =
          sanitizeProductFruitsUsername(currentUser.email) ||
          `user-${currentUser.id}`

        window.$productFruits.push([
          "init",
          "C7GD9pb7QnQZCEq2",
          "fr",
          { username },
        ])

        console.log("✅ Onboarding initialized for user:", username)
        setIsInitialized(true)
      } catch (error) {
        console.error("❌ Error initializing Onboarding:", error)
      }
    }, 500)

    return () => {
      clearTimeout(initTimeout)
    }
  }, [scriptLoaded, currentUser, isLoading, isInitialized])
}

export default useProductFruits
